import htmx from 'htmx.org';

export type MemorableDateParts = {
    month: string;
    day: string;
    year: string;
};

export function bindMemorableDates(content?: Element | Document | null | undefined) {
    // This component can be used on standalone elements, but it is meant to be used with the <memorable-date> tag helper.
    // look for [data-memorable-date].
    // [data-memorable-date-required] (set to true or false) Determines if the date is required.
    // [data-memorable-date-required-msg] Sets the required value validation message.
    // [data-memorable-date-partial-msg] Sets the validation message to use if the date is not required and the fields are only partially filled out.
    if (!content) {
        content = document;
    }

    content.querySelectorAll<HTMLElement>('[data-memorable-date]').forEach(memorableDate => {
        if (memorableDate.getAttribute('memorable-date-event') === 'true') {
            return;
        }

        const month = memorableDate.querySelector<HTMLSelectElement>('[data-memorable-date-month]');
        const day = memorableDate.querySelector<HTMLInputElement>('[data-memorable-date-day]');
        const year = memorableDate.querySelector<HTMLInputElement>('[data-memorable-date-year]');

        if (!month || !day || !year) {
            return;
        }

        memorableDate.setAttribute('memorable-date-event', 'true');

        const updateDate = () => {
            validateDateParts(memorableDate);
            updateFullDate(memorableDate);
        };
        [month, day, year].forEach(input => {
            input.addEventListener('change', updateDate, { capture: true });
            input.addEventListener('input', updateDate, { capture: true });
        });

        memorableDate.closest('form')?.addEventListener('submit', updateDate, { capture: true });
        updateDate();
    });
}

export function getMemorableDate(container: HTMLElement): string {
    const parts = getMemorableDateParts(container);

    if (!parts.month && !parts.day && !parts.year) {
        return '';
    }

    const month = Number.parseInt(parts.month, 10);
    const day = Number.parseInt(parts.day, 10);
    const year = Number.parseInt(parts.year, 10);

    if (!Number.isInteger(month) || !Number.isInteger(day) || !Number.isInteger(year) || parts.year.length !== 4) {
        return '';
    }

    const date = new Date(year, month - 1, day);

    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
        ? `${year}-${pad(month)}-${pad(day)}`
        : '';
}

export function getMemorableDateParts(container: HTMLElement): MemorableDateParts {
    return {
        month: container.querySelector<HTMLSelectElement>('[data-memorable-date-month]')?.value ?? '',
        day: container.querySelector<HTMLInputElement>('[data-memorable-date-day]')?.value ?? '',
        year: container.querySelector<HTMLInputElement>('[data-memorable-date-year]')?.value ?? ''
    };
}

function updateFullDate(container: HTMLElement) {
    const fullDate = container.querySelector<HTMLInputElement>('[data-memorable-date-full]');

    if (!fullDate) {
        return;
    }

    fullDate.value = getMemorableDate(container);
}

function validateDateParts(container: HTMLElement): boolean {
    const month = container.querySelector<HTMLSelectElement>('[data-memorable-date-month]');
    const day = container.querySelector<HTMLInputElement>('[data-memorable-date-day]');
    const year = container.querySelector<HTMLInputElement>('[data-memorable-date-year]');

    if (!month || !day || !year) {
        return true;
    }

    [month, day, year].forEach(input => input.setCustomValidity(''));

    const parts = getMemorableDateParts(container);
    const hasAnyPart = parts.month !== '' || parts.day !== '' || parts.year !== '';
    const hasAllParts = parts.month !== '' && parts.day !== '' && parts.year !== '';
    const isRequired = container.dataset.memorableDateRequired === 'true';

    if (!hasAnyPart && !isRequired) {
         return true;
    }

    if (hasAllParts) {
        if (getMemorableDate(container) === '') {
            const invalidMessage = container.dataset.memorableDateInvalidMsg ?? 'Enter a valid date.';
            [month, day, year].forEach(input => input.setCustomValidity(invalidMessage));
            return false;
        }
        return true;
    }

    const message = isRequired
        ? container.dataset.memorableDateRequiredMsg ?? ''
        : container.dataset.memorableDatePartialMsg ?? '';

    [month, day, year]
        .filter(input => input.value === '')
        .forEach(input => input.setCustomValidity(message));

    return false;
}

function pad(value: number): string {
    return value.toString().padStart(2, '0');
}

document.addEventListener('DOMContentLoaded', () => bindMemorableDates(document));
htmx.onLoad(content => bindMemorableDates(content as HTMLElement));
